import { useCallback, useRef, useState } from 'react';

import { ConsentRecordSchema, type TradingAccount, TradingAccountSchema } from '../../../api';
import type { SupportedLanguage } from '../../../i18n';
import { toApiError } from '../../../lib/api/api-client';
import { useApiClient } from '../../../lib/api/api-context';
import { newIdempotencyKey, useActionKeys } from '../../../lib/api/idempotency-key';
import type { ScreenFailure } from '../channel-selection/screen-failure';

import { CONSENT_TEXT_VERSION } from './consent-text';
import type { ConnectDraft, KeptFields } from './trading-account-view';

/** The server's 202 answer and the read it was made against. */
interface Accepted {
  readonly base: TradingAccount | undefined;
  readonly answer: TradingAccount;
}

/** What {@link useTradingAccountWrites} gives the route. */
export interface TradingAccountWrites {
  /** A `POST /trading-account` is in flight. */
  readonly connecting: boolean;
  /** The refusal of the last submit, or `null` (R28). */
  readonly connectFailure: ScreenFailure | null;
  /** The last 202, until a newer read replaces `base`: then `null` (the new read is the truth). */
  readonly overlay: TradingAccount | null;
  /** What was typed in the last submit, without the password (R28), or `null`. */
  readonly kept: KeptFields | null;
  /** A `POST /consent` is in flight. */
  readonly consenting: boolean;
  /** The refusal of the last consent, or `null` (R29). */
  readonly consentFailure: ScreenFailure | null;
  /** Grows with every 422: the consent block remounts, so its box is unchecked (R29, I7). */
  readonly consentEpoch: number;
  /** Sends the draft. The caller empties its password field before this returns (R4). */
  readonly connect: (draft: ConnectDraft) => void;
  /** Sends the consent in `language` (R14). */
  readonly consent: (language: SupportedLanguage) => void;
}

/** What the hook needs from the route. */
export interface TradingAccountWritesOptions {
  /** The read on screen: a 202 answer belongs to it and goes when it changes. */
  readonly base: TradingAccount | undefined;
  /** Starts polling at once (`useTradingAccount().watch`). */
  readonly watch: () => void;
  /** Reads the account again (`useTradingAccount().refetch`). */
  readonly reload: () => void;
  /** Called once on a 201 (R17). */
  readonly onConsented: () => void;
}

/**
 * The two writes of E4 (specs/S6-trading-account/spec.md R14, R28, R29).
 *
 * **Connect** sends a *new* `Idempotency-Key` on every submit (R28): the body holds a password the
 * screen no longer has, so a retry would send another body under the same key (409
 * `IDEMPOTENCY_KEY_REUSED`); a duplicate attempt is harmless, the API replaces the earlier one
 * (R25). The password is read from the draft once, into the request body, before the request
 * starts; nothing here keeps it (I9, R4).
 *
 * **Consent** keeps its key for a retry after a 5xx, `IDEMPOTENCY_KEY_IN_PROGRESS` or a network
 * failure, and forgets it after a 201 or a final refusal (`useActionKeys`, S2 R13).
 */
export function useTradingAccountWrites({
  base,
  watch,
  reload,
  onConsented,
}: TradingAccountWritesOptions): TradingAccountWrites {
  const api = useApiClient();
  const keys = useActionKeys('perAction');
  const connectingRef = useRef(false);
  const consentingRef = useRef(false);
  const [connecting, setConnecting] = useState(false);
  const [connectFailure, setConnectFailure] = useState<ScreenFailure | null>(null);
  const [accepted, setAccepted] = useState<Accepted | null>(null);
  const [kept, setKept] = useState<KeptFields | null>(null);
  const [consenting, setConsenting] = useState(false);
  const [consentFailure, setConsentFailure] = useState<ScreenFailure | null>(null);
  const [consentEpoch, setConsentEpoch] = useState(0);

  const connect = useCallback(
    (draft: ConnectDraft) => {
      if (connectingRef.current) return;
      connectingRef.current = true;
      // The body is built here, before the request starts; `draft` is not read again.
      const body = {
        platform: draft.platform,
        accountNumber: draft.accountNumber,
        password: draft.password,
        server: draft.server,
      };
      setKept({
        accountNumber: draft.accountNumber,
        server: draft.server,
      });
      setConnecting(true);
      setConnectFailure(null);
      api
        .post(
          '/trading-account',
          { guard: { kind: 'authenticated', idempotencyKey: newIdempotencyKey() }, body },
          TradingAccountSchema,
        )
        .then(
          (answer) => {
            connectingRef.current = false;
            setAccepted({ base, answer });
            setConnecting(false);
            watch();
          },
          (error: unknown) => {
            connectingRef.current = false;
            const failure = toApiError(error);
            setConnectFailure({ reasonCode: failure.reasonCode, params: failure.params });
            setConnecting(false);
          },
        );
    },
    [api, base, watch],
  );

  const consent = useCallback(
    (language: SupportedLanguage) => {
      if (consentingRef.current) return;
      consentingRef.current = true;
      setConsenting(true);
      setConsentFailure(null);
      const action = `consent:${language}`;
      const guard = { kind: 'authenticated', idempotencyKey: keys.keyFor(action) } as const;
      api
        .post(
          '/consent',
          { guard, body: { consentTextVersion: CONSENT_TEXT_VERSION, accepted: true, language } },
          ConsentRecordSchema,
        )
        .then(
          () => {
            keys.settle(action, null);
            consentingRef.current = false;
            setConsenting(false);
            onConsented();
          },
          (error: unknown) => {
            const failure = toApiError(error);
            keys.settle(action, failure);
            consentingRef.current = false;
            setConsenting(false);
            setConsentFailure({ reasonCode: failure.reasonCode, params: failure.params });
            // 409: the account is not verified any more, read it again. 422: the text changed,
            // uncheck the box so the trader reads it again (R29).
            if (failure.reasonCode === 'TRADING_ACCOUNT_NOT_CONNECTED') reload();
            if (failure.reasonCode === 'CONSENT_VERSION_UNKNOWN') setConsentEpoch((n) => n + 1);
          },
        );
    },
    [api, keys, onConsented, reload],
  );

  return {
    connecting,
    connectFailure,
    overlay: accepted !== null && accepted.base === base ? accepted.answer : null,
    kept,
    consenting,
    consentFailure,
    consentEpoch,
    connect,
    consent,
  };
}
